import React, { FC, Fragment, useEffect } from 'react';
import { AddressFormDetails } from 'models/contact';
import { useTranslation } from 'react-i18next';
import { Country } from 'models/country';
import { Button, Grid, TextField } from '@mui/material';
import { useFormik } from 'formik';

type PostBoxFormProps = {
    onChange: (selectedAddress: AddressFormDetails) => void;
    country: Country;
};
const PostBoxForm: FC<PostBoxFormProps> = ({ onChange, country }: PostBoxFormProps) => {
    const { t: translate } = useTranslation(['country-names']);

    useEffect(() => {
        formik.setValues({ postBox: '', postalCode: '', city: '' });
    }, [country]);

    const validate = (formData: { postBox: string; postalCode: string; city: string }) => {
        const errors: Record<string, string> = {};
        if (!formData.postBox) {
            errors.postBox = translate('organization:VALIDATION:POST_BOX');
        }
        if (!formData.postalCode) {
            errors.postalCode = translate('organization:VALIDATION:POSTAL_CODE');
        }
        if (!formData.city) {
            errors.city = translate('organization:VALIDATION:CITY');
        }

        return errors;
    };

    const formik = useFormik({
        initialValues: { postBox: '', postalCode: '', city: '' },
        validate,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: async (formData) => {
            const addressDetails = {
                googleId: '',
                address: {
                    street: formData.postBox,
                    postalCode: formData.postalCode,
                    city: formData.city,
                    country: country.alpha2Code,
                },
                isCompleteAddress: true,
            };
            onChange(addressDetails);
        },
    });

    return (
        <Fragment>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label={translate('organization:ADDRESS:POST_BOX')}
                        fullWidth
                        helperText={formik.touched.postBox && formik.errors.postBox}
                        error={formik.touched.postBox && Boolean(formik.errors.postBox)}
                        {...formik.getFieldProps('postBox')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label={translate('organization:ADDRESS:POSTAL_CODE')}
                        fullWidth
                        helperText={formik.touched.postalCode && formik.errors.postalCode}
                        error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
                        {...formik.getFieldProps('postalCode')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label={translate('organization:ADDRESS:CITY')}
                        fullWidth
                        helperText={formik.touched.city && formik.errors.city}
                        error={formik.touched.city && Boolean(formik.errors.city)}
                        {...formik.getFieldProps('city')}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button variant="contained" disabled={!formik.isValid} onClick={() => formik.submitForm()}>
                        {translate('organization:ADD_POST_BOX')}
                    </Button>
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default PostBoxForm;
